/* eslint-disable react/button-has-type */

import { PropTypes } from "prop-types"
import { Link } from "@reach/router"
import classNames from "classnames"
import _ from "lodash"

/**
 * ActionButton component used in the webapp
 * It can belong to a form, or be used as primary button
 * It is one of the most common UI elements visible on the webapp
 *
 * @param {string} accessibilityText accessibility text to be displayed as the description of the button
 * @param {node} children children of the button
 * @param {string} className class name of the button
 * @param {boolean} disabled disabled state of the button
 * @param {string} label button label to be displayed on aria-label
 * @param {string} link link to the route, in case of displaying a link instead
 * @param {func} onClick function to be called on the button click event
 * @param {string} type type of the button, e.g submit / button
 * }
 * @returns {component} returns a button or a link
 */
const ActionButton = ({
  accessibilityText,
  children,
  className,
  label,
  buttonRef,
  type = "button",
  link = null,
  disabled = false,
  onClick = _.noop
}) => {
  const renderButton = () => (
    <button
      type={type}
      className={classNames({ "state-disabled": disabled })}
      disabled={disabled}
      onClick={onClick}
      aria-label={label}
      aria-describedby={accessibilityText ? `${className}-description` : accessibilityText}
      ref={buttonRef}
    >
      {children}
    </button>
  )

  const renderLink = () => (
    <Link
      to={link}
      className={classNames({ "state-disabled": disabled })}
      onClick={onClick}
      role="button"
      aria-label={label}
      aria-describedby={accessibilityText ? `${className}-description` : accessibilityText}
      ref={buttonRef}
    >
      {children}
    </Link>
  )

  return (
    <div className={classNames("oj-action-button", className)}>
      {accessibilityText && (
        <span id={`${className}-description`} className="accessibility-tag">
          {accessibilityText}
        </span>
      )}
      {link && renderLink()}
      {!link && renderButton()}
    </div>
  )
}

ActionButton.propTypes = {
  type: PropTypes.string,
  className: PropTypes.string,
  link: PropTypes.string,
  label: PropTypes.node,
  disabled: PropTypes.bool,
  onClick: PropTypes.func,
  children: PropTypes.node,
  accessibilityText: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  buttonRef: PropTypes.shape({
    current: PropTypes.instanceOf(Element)
  })
}

export default ActionButton
